import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tasksApi } from '../../../api/tasks.api'
import { Textarea } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { Avatar } from '../../../components/ui/Avatar'
import { useAuth } from '../../../auth/AuthContext'
import { toast } from '../../../store/toastStore'

export function TaskComments({ taskId }: { taskId: string }) {
  const { me } = useAuth()
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')

  const commentsQuery = useQuery({ queryKey: ['tasks', taskId, 'comments'], queryFn: async () => (await tasksApi.listComments(taskId)).data })

  const addMutation = useMutation({
    mutationFn: () => tasksApi.addComment(taskId, body),
    onSuccess: () => {
      setBody('')
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] })
    },
    onError: () => toast.error('Kommentar konnte nicht gespeichert werden.'),
  })

  const removeMutation = useMutation({
    mutationFn: (commentId: string) => tasksApi.removeComment(taskId, commentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] }),
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {commentsQuery.data?.map((comment) => (
        <div key={comment.id} style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Avatar name={comment.author.displayName} imageUrl={comment.author.avatarUrl} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{comment.author.displayName}</span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {new Date(comment.createdAt).toLocaleString('de-DE')}
                {comment.editedAt ? ' (bearbeitet)' : ''}
              </span>
              {comment.authorId === me?.user.id ? (
                <button
                  onClick={() => removeMutation.mutate(comment.id)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 'var(--text-xs)' }}
                >
                  Löschen
                </button>
              ) : null}
            </div>
            <div style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{comment.body}</div>
          </div>
        </div>
      ))}
      {commentsQuery.data?.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Noch keine Kommentare.</span> : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Kommentar schreiben…" style={{ minHeight: 60 }} />
        <Button variant="primary" size="sm" disabled={!body.trim()} loading={addMutation.isPending} onClick={() => addMutation.mutate()}>
          Senden
        </Button>
      </div>
    </div>
  )
}
